/**
 * The Tenant Release and Hold Harmless Agreement for Tech Support (1.5.3). Counsel approved version 1.0 on 2 Oct
 * 2026 and confirmed electronic acceptance. Each tenant agrees once — electronically on the confirmation page of
 * a Tech Support appointment booked with email, or on paper (the signed copy uploaded to their record) — and the
 * app never asks again until the version changes. It covers Tech Support only (housekeeping, companion and
 * reading keep their own releases).
 *
 * The text is embedded character for character (shared/agreements-text.gen.ts, from docs/agreements/ by
 * scripts/gen-agreements.ts); tests/agreements.test.ts compares it with the document and its SHA-256.
 * A new version (1.1 and up) is a new file beside the old: the old text stays for the record, and every tenant
 * agrees again the same way.
 */
import { TECH_RELEASE_TEXTS } from './agreements-text.gen';

export const AGREEMENT_CODE = 'TECH_RELEASE';
export const AGREEMENTS_LIST = 'SM_TenantAgreements';
/** The setting slot for a Spanish text — empty until counsel approves one (then the page offers it). */
export const TECH_RELEASE_TEXT_ES_KEY = 'TECH_RELEASE_TEXT_ES';

export interface AgreementVersion {
  code: typeof AGREEMENT_CODE;
  version: string;
  text: string;
  sha256: string;
  file: string;
}

export const TECH_RELEASE_VERSIONS: readonly AgreementVersion[] = TECH_RELEASE_TEXTS.map((t) => ({ code: AGREEMENT_CODE, ...t }));
/** The version every tenant must have on file (the newest). */
export const TECH_RELEASE_CURRENT: AgreementVersion = TECH_RELEASE_VERSIONS[TECH_RELEASE_VERSIONS.length - 1]!;

export function agreementVersion(version: string): AgreementVersion | null {
  return TECH_RELEASE_VERSIONS.find((v) => v.version === version) ?? null;
}

/* ---------------- the text as the page shows it ---------------- */

export type AgreementBlock = { kind: 'p'; text: string } | { kind: 'list'; items: string[] };

export interface AgreementSection {
  n: number;
  title: string;
  blocks: AgreementBlock[];
  paragraphs: string[];
  items: string[];
}

export interface ParsedAgreement {
  org: string;
  title: string;
  subtitle: string;
  place: string;
  plainWords: string;
  intro: string;
  sections: AgreementSection[];
  /** The signature lines of the paper form ("Tenant Name (print): Unit Number:" …). */
  signature: string[];
}

const unmd = (s: string) => s.replace(/\*\*/g, '').replace(/\\([()[\]*_#>.-])/g, '$1').trim();

/** The document's parts in order: header, plain words, the opening paragraph, sections 1–9, the signature lines. */
export function parseAgreement(text: string): ParsedAgreement {
  const blocks = text.replace(/\r\n/g, '\n').split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  const out: ParsedAgreement = { org: '', title: '', subtitle: '', place: '', plainWords: '', intro: '', sections: [], signature: [] };
  const head: string[] = [];
  let current: AgreementSection | null = null;
  for (const b of blocks) {
    const sec = /^\*\*(\d+)\.\s+(.+?)\*\*$/.exec(b);
    if (sec) {
      current = { n: Number(sec[1]), title: sec[2]!.trim(), blocks: [], paragraphs: [], items: [] };
      out.sections.push(current);
      continue;
    }
    if (/^Tenant (Name|Signature)\b/.test(b)) {
      out.signature.push(unmd(b));
      continue;
    }
    if (b.startsWith('**In plain words:**')) {
      out.plainWords = unmd(b.slice('**In plain words:**'.length));
      continue;
    }
    if (current) {
      if (b.startsWith('>')) {
        const items = b
          .split('\n')
          .map((l) => unmd(l.replace(/^>\s?/, '')))
          .filter(Boolean);
        current.blocks.push({ kind: 'list', items });
        current.items.push(...items);
      } else {
        const p = unmd(b);
        current.blocks.push({ kind: 'p', text: p });
        current.paragraphs.push(p);
      }
      continue;
    }
    if (out.plainWords) out.intro = [out.intro, unmd(b)].filter(Boolean).join('\n\n');
    else head.push(unmd(b));
  }
  [out.org = '', out.title = '', out.subtitle = '', out.place = ''] = head;
  return out;
}

/** "Services" from "SERVICES" — the headings as a screen reader should say them (the page keeps the document's case). */
export function sectionWords(s: AgreementSection): string {
  return `${s.n}. ${s.title.charAt(0)}${s.title.slice(1).toLowerCase()}`;
}

/** What Read this to me says, part by part (each part is highlighted while it is read). */
export function speakableParts(p: ParsedAgreement): { id: string; text: string }[] {
  return [
    { id: 'title', text: `${p.title}. ${p.subtitle}.` },
    { id: 'plain', text: `In plain words: ${p.plainWords}` },
    { id: 'intro', text: p.intro },
    ...p.sections.map((s) => ({ id: `s${s.n}`, text: [`${sectionWords(s)}.`, ...s.blocks.map((b) => (b.kind === 'p' ? b.text : b.items.join(' ')))].join(' ') })),
  ];
}

/* ---------------- the tenant's status, from the append-only rows ---------------- */

export type AgreementMethod = 'Electronic' | 'Paper';
export const AGREEMENT_METHODS: readonly AgreementMethod[] = ['Electronic', 'Paper'];

/** One SM_TenantAgreements row as the app reads it. A row with RevokedAt is a revocation (written notice). */
export interface AgreementRecord {
  id: number;
  version: string;
  method: AgreementMethod;
  agreedAt: string | null;
  signedOn: string | null;
  appointmentId: string | null;
  fileUrl: string | null;
  recordedBy: string | null;
  revokedAt: string | null;
  revokedNote: string | null;
  notes: string | null;
}

export type AgreementStatus = 'Electronic' | 'Paper' | 'None';

export interface AgreementState {
  status: AgreementStatus;
  /** On file for the current version (not revoked): the app never asks again. */
  onFile: boolean;
  current: string;
  /** The date shown: the paper's signed date, else when it was agreed (ISO). */
  date: string | null;
  agreedAt: string | null;
  version: string | null;
  method: AgreementMethod | null;
  appointmentId: string | null;
  fileUrl: string | null;
  recordedBy: string | null;
  /** The row on file (Open the stored copy). */
  rowId: number | null;
  /** The last revocation, while nothing has been agreed since. */
  revoked: { at: string; note: string | null } | null;
  /** "Agreed to 1.0 — 1.1 not yet agreed": the newest older version agreed, when the current one is not. */
  olderVersion: string | null;
}

const at = (r: AgreementRecord) => r.revokedAt ?? r.agreedAt ?? '';

export function agreementState(rows: readonly AgreementRecord[], current: string = TECH_RELEASE_CURRENT.version): AgreementState {
  const sorted = [...rows].sort((a, b) => at(a).localeCompare(at(b)) || a.id - b.id);
  let active: AgreementRecord | null = null;
  let older: AgreementRecord | null = null;
  let revoked: AgreementState['revoked'] = null;
  for (const r of sorted) {
    if (r.revokedAt) {
      active = null;
      older = null;
      revoked = { at: r.revokedAt, note: r.revokedNote };
    } else if (r.version === current) {
      active = r;
      revoked = null;
    } else {
      older = r;
      revoked = null;
    }
  }
  const base = { current, revoked, olderVersion: active ? null : (older?.version ?? null) };
  if (!active) return { ...base, status: 'None', onFile: false, date: null, agreedAt: null, version: null, method: null, appointmentId: null, fileUrl: older?.fileUrl ?? null, recordedBy: null, rowId: older?.id ?? null };
  return {
    ...base,
    status: active.method,
    onFile: true,
    date: active.method === 'Paper' ? (active.signedOn ?? active.agreedAt) : active.agreedAt,
    agreedAt: active.agreedAt,
    version: active.version,
    method: active.method,
    appointmentId: active.appointmentId,
    fileUrl: active.fileUrl,
    recordedBy: active.recordedBy,
    rowId: active.id,
  };
}

/* ---------------- what the screens and the API exchange ---------------- */

/** The exact refusal on Book (counsel's wording — keep it). */
export const PHONE_BOOKING_REFUSAL = 'This tenant has no signed agreement on file. The tenant must come in and sign before an appointment can be booked by phone or in person.';

export interface TenantAgreementView {
  /** False until scripts/add-tenant-agreements-list.ps1 has run: the feature is dark. */
  ready: boolean;
  tenantId: string;
  state: AgreementState;
  /** Every row, newest first (nothing on it can be edited or deleted). */
  history: (AgreementRecord & { kind: 'agreement' | 'revocation' })[];
}

export interface AgreementReportRow {
  tenantId: string;
  name: string;
  unit: string;
  status: AgreementStatus;
  date: string | null;
  version: string | null;
  method: AgreementMethod | null;
  revoked: boolean;
  olderVersion: string | null;
  /** Tech Support appointments ahead that wait for the agreement. */
  awaiting: number;
}

export interface AgreementReport {
  ready: boolean;
  current: string;
  rows: AgreementReportRow[];
}

/** The copy's file name: "2026-10-02 Last, First 6C Release v1.0.pdf" (date first, REQUIREMENTS §9). */
export function agreementFileName(dateIso: string, last: string, first: string, unit: string, version: string): string {
  const clean = (s: string) => s.replace(/[\\/:*?"<>|#%]/g, ' ').replace(/\s+/g, ' ').trim();
  return `${dateIso.slice(0, 10)} ${clean(last) || 'Tenant'}, ${clean(first) || 'Tenant'} ${clean(unit)} Release v${version}.pdf`.replace(/\s+/g, ' ');
}

/** The morning-of reminder's line while the tenant has not agreed (added when it is sent — the same link below). */
export const AGREEMENT_REMINDER_LINE = {
  en: 'Not confirmed yet: your Tech Support visit is not confirmed until you read and accept the Tenant Release and Hold Harmless Agreement. Open the link below, tick the box, then press Confirm.',
  es: 'Aún no está confirmada: su visita de Soporte técnico no queda confirmada hasta que lea y acepte el Acuerdo de Liberación y Exoneración del Inquilino. Abra el enlace de abajo, marque la casilla y luego pulse Confirmar.',
} as const;

/** The tenant folder's agreements sub-folder. */
export const AGREEMENTS_FOLDER = 'Tech Support/Agreements';
